// Author: Ashish Kumar Founder@InnovNet
import { Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet, Router, RouterLink, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ErrorModalComponent } from './shared/error-modal/error-modal.component';
import { ModalComponent } from './components/modal/modal.component';
import { AIAssistantModalComponent } from './shared/ai-assistant-modal/ai-assistant-modal.component';
import { UpgradePromptComponent } from './shared/upgrade-prompt/upgrade-prompt.component';
import { AppSidebarComponent } from './app-sidebar/app-sidebar.component';
import { CommandPaletteComponent } from './home/command-palette.component';
import { UiThemeControlComponent } from './shared/ui/ui-theme-control.component';
import { MenuToggleIconComponent } from './shared/menu-toggle-icon/menu-toggle-icon.component';
import { HttpClient } from '@angular/common/http';
import { HubSpotService } from './hubspot.service';
import { AuthService } from './auth.service';
import { ConfigurationService } from './services/configuration.service';
import { ThemeService } from './shared/theme.service';
import { ReferralCaptureService } from './shared/referral-capture.service';
import { RESERVED_PROFILE_PATHS } from './app.routes';
import { WelcomeService } from './welcome.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, RouterLink, CommonModule, ErrorModalComponent, ModalComponent, AIAssistantModalComponent, UpgradePromptComponent, AppSidebarComponent, MenuToggleIconComponent, UiThemeControlComponent, CommandPaletteComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'InnovNet';
  currentRoute: string = '';
  showScrollToTop: boolean = false;
  sidebarOpen = false;
  sidebarCollapsed = false;
  isDesktop = false;
  private readonly sidebarCollapsedKey = 'innovnet.sidebarCollapsed';
  trialBanner: { ended: boolean; daysLeft: number | null; endsOn: string | null } | null = null;

  constructor(
    private router: Router,
    private hubspotService: HubSpotService,
    private authService: AuthService,
    private http: HttpClient,
    private configService: ConfigurationService,
    private referralCapture: ReferralCaptureService,
    private welcomeNav: WelcomeService
  ) {
    inject(ThemeService);
    this.sidebarCollapsed = this.readSidebarCollapsed();
    this.syncViewport();
  }

  loadTrialBanner(): void {
    if (!this.authService.isAuthenticated() || !this.shouldShowAppShell()) {
      this.trialBanner = null;
      return;
    }
    this.http.post<{ showBanner?: boolean; ended?: boolean; daysLeft?: number | null; endsOn?: string | null }>(
      `${this.configService.getApiBaseUrl()}/get_trial_status.php`,
      {},
      { withCredentials: true }
    ).subscribe({
      next: (response) => {
        this.trialBanner = response?.showBanner
          ? {
              ended: !!response.ended,
              daysLeft: response.daysLeft ?? null,
              endsOn: response.endsOn ?? null,
            }
          : null;
      },
      error: () => {
        this.trialBanner = null;
      },
    });
  }

  ngOnInit(): void {
    this.currentRoute = this.router.url;
    this.referralCapture.captureFromUrl(this.router.url);

    // Auto-connect to HubSpot if credentials exist
    this.autoConnectToHubSpot();
    
    // Listen for window focus events (when user returns to tab)
    this.setupWindowFocusListener();
    
    // Listen for storage events (when auth state changes in other tabs)
    this.setupStorageListener();
    
    // Check authentication on initial load
    this.checkInitialAuthentication();
    this.loadTrialBanner();
    
    // Listen for route changes
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = event.urlAfterRedirects;
        this.referralCapture.captureFromUrl(event.urlAfterRedirects);
        this.sidebarOpen = false;
        this.syncSidebarBodyClass();
        // Check if user is on login page but already authenticated
        this.checkAuthAndRedirect();
        this.loadTrialBanner();
      }
    });
    this.syncSidebarBodyClass();

    // Listen for scroll events to show/hide scroll-to-top button
    this.setupScrollListener();
  }

  /**
   * Check authentication status on initial app load
   */
  private checkInitialAuthentication(): void {
    // Skip authentication check for public routes (customized-frontend, exchangeContact, etc.)
    const publicRoutes = [
      '/signin', 
      '/signup', 
      '/forgot-password', 
      '/verify-email', 
      '/validateAccount',
      '/un-verified-email',
      '/exchangeContact',
      '/payment-success',
      '/payment-cancel',
      '/integrations/hubspot/callback',
      '/hubspot-callback',
      '/integrations/salesforce/callback',
      '/notFound',
      '/somethingWentWrong',
      '/track-order',
      '/feedback'
    ];
    
    // Check if current route is a public route or a customized-frontend route (hex string)
    const pathOnly = this.currentRoute.split('?')[0].split('#')[0];
    const cardSegment = pathOnly.replace(/^\//, '');
    const isPublicRoute = publicRoutes.some(route => pathOnly === route || pathOnly.startsWith(`${route}/`)) ||
                         (/^[a-zA-Z0-9_-]+$/.test(cardSegment) && !RESERVED_PROFILE_PATHS.has(cardSegment));
    
    if (isPublicRoute) {
      // Don't check authentication for public routes
      return;
    }
    
    // Small delay to ensure all services are initialized
    setTimeout(() => {
      this.authService.shouldRedirectFromLogin().subscribe(shouldRedirect => {
        if (shouldRedirect && (this.currentRoute === '/' || this.currentRoute === '/signin')) {
          this.welcomeNav.destination().subscribe((path) => this.router.navigateByUrl(path));
        }
      });
    }, 100);
  }

  /**
   * Setup listener for storage events to sync authentication state across tabs
   */
  private setupStorageListener(): void {
    window.addEventListener('storage', (event) => {
      
      // Check if authentication state changed in another tab
      if (event.key === 'deviceToken' || event.key === 'appSharedData' || event.key === 'innovnet_auth_state') {
        
        // Only sync state if we don't already have a valid session
        // This prevents continuous API calls when the user is already authenticated
        const currentUser = this.authService.getCurrentUser();
        if (!currentUser) {
          // Only check session if we don't have current user data
          this.authService.forceCheckSession().subscribe({
            next: (response) => {
              if (response.status === 'success') {
                // Just sync the state, don't trigger additional checks
                // The auth service will automatically update the observables
              }
            },
            error: (error) => {
            }
          });
        }
      }
    });
  }

  /**
   * Setup listener for window focus events to check authentication when user returns to tab
   */
  private setupWindowFocusListener(): void {
    window.addEventListener('focus', () => {
      
      // Check if user is on login page and should be redirected
      if (this.currentRoute === '/' || this.currentRoute === '/signin') {
        // Check localStorage for authentication state from other tabs
        this.authService.shouldRedirectFromLogin().subscribe(shouldRedirect => {
          if (shouldRedirect) {
            this.welcomeNav.destination().subscribe((path) => this.router.navigateByUrl(path));
          }
        });
      }
    });
  }

  /**
   * Automatically connect to HubSpot when app loads
   */
  private autoConnectToHubSpot(): void {
    // Small delay to ensure app is fully loaded
    setTimeout(() => {
      this.hubspotService.autoConnectToHubSpot().then((connected) => {
        // HubSpot connection status handled silently
      });
    }, 1000);
  }

  /**
   * Check if user is authenticated and redirect from login page if needed
   */
  private checkAuthAndRedirect(): void {
    // Only check if we're on a login page (exclude signup and other registration pages)
    const loginRoutes = ['/', '/signin'];
    
    // Only proceed if we have a valid route and it's a login route
    if (this.currentRoute && loginRoutes.includes(this.currentRoute)) {
      // Use the shouldRedirectFromLogin method for better authentication checking
      this.authService.shouldRedirectFromLogin().subscribe(shouldRedirect => {
        if (shouldRedirect) {
          this.welcomeNav.destination().subscribe((path) => this.router.navigateByUrl(path));
        }
      });
    }
  }

  /** `/admin` and every child route use their own admin shell instead of the main app sidebar. */
  isAdminRoute(): boolean {
    const pathOnly = this.currentRoute.split('?')[0].split('#')[0];
    return pathOnly === '/admin' || pathOnly.startsWith('/admin/');
  }

  shouldShowAppShell(): boolean {
    if (this.isAdminRoute()) {
      return false;
    }
    const fullscreenRoutes = ['/scan'];
    if (fullscreenRoutes.some((route) => this.currentRoute === route || this.currentRoute.startsWith(`${route}/`))) {
      return false;
    }

    const authRoutes = ['/', '/signin', '/signup', '/onboarding', '/welcome', '/forgot-password', '/verify-email', '/validateAccount', '/un-verified-email', '/feedback'];
    if (authRoutes.some(route => this.currentRoute === route)) {
      return false;
    }

    if (this.currentRoute.startsWith('/resetPassword')) {
      return false;
    }

    const appPrefixes = [
      '/home',
      '/insights',
      '/customTheme',
      '/newuiprofile',
      '/templateSelector',
      '/exchangedContacts',
      '/card-mode',
      '/events',
      '/payment',
      '/settings',
      '/refer',
      '/editor',
      '/admin',
      '/scan',
      '/contact-detail',
      '/visitor-details',
    ];
    const pathOnly = this.currentRoute.split('?')[0].split('#')[0];
    if (/^\/[^\/]+$/.test(pathOnly) && !appPrefixes.some((prefix) => pathOnly === prefix || pathOnly.startsWith(`${prefix}/`))) {
      return false;
    }

    return true;
  }

  get isAdmin(): boolean {
    return this.authService.isAdmin;
  }

  get isSidebarVisible(): boolean {
    return this.isDesktop ? !this.sidebarCollapsed : this.sidebarOpen;
  }

  get sidebarToggleLabel(): string {
    return this.isSidebarVisible ? 'Collapse sidebar' : 'Expand sidebar';
  }

  toggleSidebar(): void {
    if (this.isDesktop) {
      this.setSidebarCollapsed(!this.sidebarCollapsed);
      return;
    }
    this.sidebarOpen = !this.sidebarOpen;
  }

  closeSidebar(): void {
    this.sidebarOpen = false;
  }

  setSidebarCollapsed(collapsed: boolean): void {
    this.sidebarCollapsed = collapsed;
    try {
      localStorage.setItem(this.sidebarCollapsedKey, collapsed ? '1' : '0');
    } catch {
      // Storage may be unavailable
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.syncViewport();
  }

  ngOnDestroy(): void {
    document.body.classList.remove('app-has-sidebar', 'admin-portal');
  }

  private syncSidebarBodyClass(): void {
    // Admin pages hide the app sidebar but keep the signed-in token remap (light/dark) that this class carries.
    document.body.classList.toggle('app-has-sidebar', this.shouldShowAppShell() || this.isAdminRoute());
    document.body.classList.toggle('admin-portal', this.isAdminRoute());
  }

  private syncViewport(): void {
    this.isDesktop = typeof window !== 'undefined' && window.innerWidth > 768;
    if (this.isDesktop) {
      this.sidebarOpen = false;
    }
  }

  private readSidebarCollapsed(): boolean {
    try {
      return localStorage.getItem(this.sidebarCollapsedKey) === '1';
    } catch {
      return false;
    }
  }

  shouldShowScrollToTop(): boolean {
    if (this.currentRoute.startsWith('/scan')) {
      return false;
    }
    return this.showScrollToTop;
  }

  /**
   * Scroll to top of the page smoothly
   */
  scrollToTop(): void {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  /**
   * Setup scroll listener to show/hide scroll-to-top button
   */
  private setupScrollListener(): void {
    window.addEventListener('scroll', () => {
      this.showScrollToTop = window.scrollY > 300;
    });
  }
}
